/* eslint-disable no-console */
import { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import FonemaModelMC from '../../models/FonemaModelMC';
import { getFonemaWhatsappToken } from './infrastructure/config/fonemaEnvConfig';
import { canUserMakeCalls, canUserReceiveCalls } from '../../utils/callsPermissionsUtils';

/**
 * Hook para obtener los datos necesarios para useFonemaWhatsappAudio.
 * @returns {Object} Objeto con token, defaultTo, defaultFrom, incomingFrom
 * @returns {string} return.token
 * @returns {string} return.defaultTo
 * @returns {string} return.defaultFrom
 * @returns {string} return.incomingFrom
 */

export default function useFonemaWhatsappAudioData() {
  const { retailer_info } = useSelector((reduxState) => reduxState.retailerUsersReducer);
  const currentRetailerUser = useSelector((state) => state.mainReducer?.currentRetailerUser);
  const token = getFonemaWhatsappToken();
  const [defaultFrom, setDefaultFrom] = useState();
  const [isSipEnabledFonema, setIsSipEnabledFonema] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetchSipEnabledStatus = async () => {
      try {
        const result = await FonemaModelMC.get();
        if (isMounted) {
          setIsSipEnabledFonema(result?.sipEnabled || false);
        }
      } catch (error) {
        console.error("Error checking SIP enabled status:", error);
      }
    };
    fetchSipEnabledStatus();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (retailer_info?.phone_number) {
      // Normaliza el número para que tenga solo un '+' al inicio
      let phone = retailer_info.phone_number.trim();
      phone = phone.replace(/^\++/, ''); // elimina todos los + iniciales
      setDefaultFrom(`+${phone}`);
    }
  }, [retailer_info?.phone_number]);

  const canReceiveCalls = useMemo(
    () => canUserReceiveCalls(currentRetailerUser),
    [currentRetailerUser]
  );
  const canMakeCalls = useMemo(
    () => canUserMakeCalls(currentRetailerUser),
    [currentRetailerUser]
  );

  // Init/register regardless of user call permissions; those are checked at call time.
  const isFonemaEnabled = useMemo(
    () => retailer_info?.fonema_whatsapp_calls_enabled && isSipEnabledFonema,
    [retailer_info?.fonema_whatsapp_calls_enabled, isSipEnabledFonema]
  );

  return {
    /**
     * Token de autenticación para el SDK de Fonema.
     * @type {string}
     */
    token,
    /**
     * Número de teléfono origen por defecto.
     * @type {string}
     */
    defaultFrom,
    /**
     * Manejador para actualizar el número origen.
     * @type {function(string): void}
     */
    setDefaultFrom,
    /**
     * Indica si el retailer puede usar Fonema.
     * @type {boolean}
     */
    isFonemaEnabled,
    canReceiveCalls,
    canMakeCalls
  };
}
